import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ExternalLink, ChevronDown, FileText } from "lucide-react";

const money = (n) => `$${Number(n).toFixed(2)}`;
const PORTAL_SIGNUP_URL = "https://portal.pttnz.net/register";

/** Converts a plan's min_months validity into the right price suffix —
 * 1 (or missing) = billed monthly, 12 = a once-off annual price, anything
 * else = a once-off price valid for that many months. */
export const priceSuffix = (minMonths) => {
  if (!minMonths || minMonths === 1) return "/month";
  if (minMonths === 12) return "/yr";
  return `/${minMonths}mo`;
};

/** Shared plan pricing card — consumes the live Portal plan shape
 * { id, name, data_allowance, price, description, terms, min_months }. Used
 * on Plans.jsx and RVMotorhome.jsx so both stay in sync with the live catalogue. */
export const PlanCard = ({ plan, interestLabel }) => {
  const [termsOpen, setTermsOpen] = useState(false);
  return (
    <div
      data-testid={`plan-card-${plan.id}`}
      className={`relative flex flex-col p-5 border transition-colors ${plan.featured ? "border-[var(--ptt-teal-deep)] bg-[var(--ptt-teal)]/[0.07]" : "border-[var(--ptt-line)] bg-white hover:border-[var(--ptt-ink)]"}`}
    >
      {plan.featured && (
        <div className="absolute -top-3 left-4 bg-[var(--ptt-teal-deep)] text-white text-[10px] font-mono-tech uppercase tracking-widest px-2.5 py-1">Popular</div>
      )}
      <div className="font-mono-tech text-[10px] uppercase tracking-widest text-[var(--ptt-muted)]">{plan.data_allowance || plan.data}</div>
      <h3 className="font-display text-base font-bold mt-1.5 leading-snug min-h-[2.5rem]">{plan.name}</h3>
      <div className="mt-3 flex items-baseline gap-1">
        {plan.price != null ? (
          <>
            <span className="font-display text-[1.75rem] font-bold">{money(plan.price)}</span>
            <span className="text-xs text-[var(--ptt-muted)]">{priceSuffix(plan.min_months)}</span>
          </>
        ) : (
          <span className="font-display text-lg font-bold text-[var(--ptt-teal-deep)]">Contact us</span>
        )}
      </div>
      {(plan.description || plan.note) && <p className="text-xs text-[var(--ptt-muted)] mt-2 leading-relaxed">{plan.description || plan.note}</p>}

      {plan.price != null ? (
        <a
          href={PORTAL_SIGNUP_URL}
          target="_blank"
          rel="noopener noreferrer"
          data-testid={`plan-cta-${plan.id}`}
          className="mt-4 pt-4 border-t border-[var(--ptt-line)] text-sm font-semibold inline-flex items-center gap-1.5 signal-underline"
        >
          Sign up <ExternalLink size={13} />
        </a>
      ) : (
        <Link
          to={`/contact?interest=${encodeURIComponent(interestLabel || "")}&plan=${encodeURIComponent(plan.name)}`}
          data-testid={`plan-cta-${plan.id}`}
          className="mt-4 pt-4 border-t border-[var(--ptt-line)] text-sm font-semibold inline-flex items-center gap-1.5 signal-underline"
        >
          Enquire <ArrowRight size={13} />
        </Link>
      )}

      {plan.terms && (
        <div className="mt-3 pt-3 border-t border-dashed border-[var(--ptt-line)]">
          <button
            onClick={() => setTermsOpen((v) => !v)}
            data-testid={`plan-terms-toggle-${plan.id}`}
            className="text-[11px] font-semibold text-[var(--ptt-muted)] hover:text-[var(--ptt-ink)] inline-flex items-center gap-1.5 transition-colors"
          >
            <FileText size={11} /> View plan terms <ChevronDown size={11} className={`transition-transform ${termsOpen ? "rotate-180" : ""}`} />
          </button>
          {termsOpen && (
            <p className="text-[10px] text-[var(--ptt-muted)] mt-2 leading-relaxed" data-testid={`plan-terms-${plan.id}`}>{plan.terms}</p>
          )}
        </div>
      )}
    </div>
  );
};

export const PlansUnavailable = () => (
  <div className="border border-dashed border-[var(--ptt-line)] bg-[var(--ptt-paper)] p-8 text-center" data-testid="plans-unavailable">
    <p className="font-display text-lg font-bold">Plans are temporarily unavailable</p>
    <p className="text-sm text-[var(--ptt-muted)] mt-2 max-w-md mx-auto">We couldn't load live pricing right now. Please contact us directly and we'll get you current pricing straight away.</p>
    <Link to="/contact" data-testid="plans-unavailable-contact" className="ptt-button px-5 py-3 text-sm font-bold inline-flex items-center gap-2 mt-5">Contact us <ArrowRight size={14} /></Link>
  </div>
);

export const PlansEmpty = ({ interestLabel }) => (
  <div className="border border-dashed border-[var(--ptt-line)] bg-[var(--ptt-paper)] p-8 text-center" data-testid="plans-empty">
    <p className="font-display text-lg font-bold">No published plans in this category yet</p>
    <p className="text-sm text-[var(--ptt-muted)] mt-2 max-w-md mx-auto">Get in touch and we'll put together a plan that fits what you need.</p>
    <Link to={`/contact?interest=${encodeURIComponent(interestLabel || "")}`} data-testid="plans-empty-contact" className="ptt-button px-5 py-3 text-sm font-bold inline-flex items-center gap-2 mt-5">Contact us <ArrowRight size={14} /></Link>
  </div>
);

export const PlansLoading = () => (
  <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5" data-testid="plans-loading">
    {[0, 1, 2, 3].map((i) => (
      <div key={i} className="p-5 border border-[var(--ptt-line)] bg-white animate-pulse">
        <div className="h-2.5 w-12 bg-[var(--ptt-line)]" />
        <div className="h-4 w-3/4 bg-[var(--ptt-line)] mt-3" />
        <div className="h-7 w-1/2 bg-[var(--ptt-line)] mt-4" />
        <div className="h-3 w-full bg-[var(--ptt-line)] mt-4" />
      </div>
    ))}
  </div>
);
